/**
 * BuilderSidebar — Tab bar + content router + persistent chat input.
 * Extracted from BuilderPage.js to reduce main file by ~200 lines.
 */
import { memo } from 'react';
import { Layers, Image as ImageIcon, Palette, Type, MessageSquare, FileText, Send, Loader2 } from 'lucide-react';
import SectionsTab from './tabs/SectionsTab';
import MediaTab from './tabs/MediaTab';
import ColorsTab from './tabs/ColorsTab';
import FontsTab from './tabs/FontsTab';
import ChatTab from './tabs/ChatTab';
import BlogTab from './tabs/BlogTab';

const TABS = [
  { key: 'sections', icon: Layers, label: 'Layout' },
  { key: 'media', icon: ImageIcon, label: 'Photos' },
  { key: 'colors', icon: Palette, label: 'Colors' },
  { key: 'fonts', icon: Type, label: 'Fonts' },
  { key: 'chat', icon: MessageSquare, label: 'Iris' },
  { key: 'blog', icon: FileText, label: 'Blog' },
];

const BuilderSidebar = memo(({
  tab, setTab, theme, t, inputStyle, siteId,
  // Sections
  sections, activeSection, setActiveSection, sectionChat, setSectionChat,
  sending, currentSuggestion, loadingSuggestion, fetchSuggestion,
  onSectionChat, onQuickAction, onReorder, userPlan, onShowPricing, onShowGallery,
  // Media
  mediaItems, swappingMedia, setSwappingMedia, mediaVideoUrl, setMediaVideoUrl,
  onMediaSwap, onMediaUploadAndSwap,
  // Colors & Fonts
  colors, onColorSwap, fonts, onFontSwap,
  // Chat
  chatHistory, fullChat, setFullChat, onChatSubmit, activePageName,
  // Blog
  onShowPricingForBlog,
  // Quick chat handler
  onQuickChat,
}) => (
  <div style={{ width: '400px', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${t.border}`, flexShrink: 0, background: t.sidebar || 'transparent', position: 'relative' }}>

    {/* Tab bar — premium pill-style */}
    <div style={{ display: 'flex', gap: '2px', padding: '8px 12px', borderBottom: `1px solid ${t.border}`, flexShrink: 0, background: theme === 'light' ? 'rgba(26,25,23,0.02)' : 'rgba(242,239,233,0.015)' }}>
      {TABS.map(tb => (
        <button key={tb.key} onClick={() => setTab(tb.key)} style={{
          flex: 1, padding: '8px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
          background: tab === tb.key ? (theme === 'light' ? '#fff' : 'rgba(242,239,233,0.06)') : 'transparent',
          border: 'none', cursor: 'pointer', borderRadius: '8px',
          color: tab === tb.key ? t.fg : t.muted,
          boxShadow: tab === tb.key ? (theme === 'light' ? '0 1px 3px rgba(0,0,0,0.06)' : '0 1px 3px rgba(0,0,0,0.3)') : 'none',
          transition: 'all .2s cubic-bezier(.25,.46,.45,.94)',
        }}
        onMouseEnter={e => { if (tab !== tb.key) e.currentTarget.style.background = theme === 'light' ? 'rgba(26,25,23,0.03)' : 'rgba(242,239,233,0.03)'; }}
        onMouseLeave={e => { if (tab !== tb.key) e.currentTarget.style.background = 'transparent'; }}
        >
          <tb.icon size={15} strokeWidth={tab === tb.key ? 2 : 1.5} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: '11px', fontWeight: tab === tb.key ? 500 : 400 }}>{tb.label}</span>
        </button>
      ))}
    </div>

    {/* Tab content */}
    <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
      {tab === 'sections' && (
        <SectionsTab sections={sections} activeSection={activeSection} setActiveSection={setActiveSection}
          sectionChat={sectionChat} setSectionChat={setSectionChat} sending={sending}
          currentSuggestion={currentSuggestion} loadingSuggestion={loadingSuggestion}
          fetchSuggestion={fetchSuggestion} onSectionChat={onSectionChat} onQuickAction={onQuickAction}
          onReorder={onReorder} userPlan={userPlan} onShowPricing={onShowPricing}
          onShowGallery={onShowGallery} t={t} theme={theme} inputStyle={inputStyle} />
      )}
      {tab === 'media' && (
        <MediaTab mediaItems={mediaItems} swappingMedia={swappingMedia} setSwappingMedia={setSwappingMedia}
          mediaVideoUrl={mediaVideoUrl} setMediaVideoUrl={setMediaVideoUrl}
          onMediaSwap={onMediaSwap} onMediaUploadAndSwap={onMediaUploadAndSwap} t={t} theme={theme} />
      )}
      {tab === 'colors' && <ColorsTab colors={colors} onColorSwap={onColorSwap} t={t} />}
      {tab === 'fonts' && <FontsTab fonts={fonts} onFontSwap={onFontSwap} t={t} theme={theme} />}
      {tab === 'chat' && (
        <ChatTab chatHistory={chatHistory} fullChat={fullChat} setFullChat={setFullChat}
          sending={sending} onSubmit={onChatSubmit} activePageName={activePageName} t={t} theme={theme} inputStyle={inputStyle} />
      )}
      {tab === 'blog' && (
        <BlogTab siteId={siteId} userPlan={userPlan} onShowPricing={onShowPricingForBlog} t={t} theme={theme} />
      )}
    </div>

    {/* Persistent "Ask Iris" input — visible on all tabs except Chat */}
    {tab !== 'chat' && userPlan?.plan !== 'free' && (
      <div style={{ padding: '12px 16px', borderTop: `1px solid ${t.border}`, flexShrink: 0, background: theme === 'light' ? t.surface : 'rgba(242,239,233,0.02)' }}>
        <form onSubmit={e => { e.preventDefault(); const input = e.target.elements.quickChat; if (input.value.trim()) { onQuickChat(input.value.trim()); input.value = ''; } }} style={{ display: 'flex', gap: '8px' }}>
          <input
            name="quickChat"
            placeholder="Ask Iris anything..."
            data-testid="quick-chat-input"
            style={{ flex: 1, padding: '10px 14px', fontSize: '13px', fontFamily: 'var(--sans)', background: t.inputBg, border: `1px solid ${t.border}`, color: t.fg, outline: 'none', borderRadius: '8px', transition: 'border-color .2s' }}
            onFocus={e => e.currentTarget.style.borderColor = 'rgba(201,168,76,0.3)'}
            onBlur={e => e.currentTarget.style.borderColor = t.border}
          />
          <button type="submit" disabled={sending} style={{ padding: '10px 14px', background: sending ? t.faint : t.gold, color: sending ? t.muted : '#000', border: 'none', cursor: sending ? 'wait' : 'pointer', borderRadius: '8px', display: 'flex', alignItems: 'center', transition: 'all .15s', boxShadow: sending ? 'none' : '0 2px 6px rgba(201,168,76,0.15)' }}>
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </form>
      </div>
    )}
  </div>
));

BuilderSidebar.displayName = 'BuilderSidebar';
export default BuilderSidebar;
